import React, { useState, useRef, useEffect } from "react";
import "./clientTextMesDisplay.css";
import { SEND_SMS_TEXT_STRIPPED_RETURN } from "../../../../utils/GraphQL/mutations";
import { useMutation } from "@apollo/client";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { useMediaQuery } from "@mui/material";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import smsErrorMessage from "../../../../utils/smsErrorMessage";

const ClientTextMesDisplay = ({
  textMesIncArr,
  closeMessages,
  selectedPhnId,
  /* The console's VS supplies its own header; see the note by the header. */
  hideHeader,
}) => {
  const { handleSnack } = useSnackbar();
  const [sendSMSgql] = useMutation(SEND_SMS_TEXT_STRIPPED_RETURN, {
    fetchPolicy: "network-only",
  });
  const [shadowOn, setShadowOn] = useState(false);
  const smaller900 = useMediaQuery("(max-width:900px)");
  const [refocusMes, setRefocusMes] = useState(false);
  const [sendMesInput, setSendMesInput] = useState("");
  const [textMes, setTextMes] = useState([]);
  const [selectedNum, setSelectedNum] = useState(0);
  // Failed outbound bubbles (optimistic sends the server rejected), keyed by
  // the selected phone id so switching numbers doesn't cross-show them.
  const [failedSends, setFailedSends] = useState([]);
  // `${phnId}|${body}` keys with a send currently in flight — Retry taps for
  // these are no-ops until the send settles (prevents double-fire duplicates).
  const sendInFlightRef = useRef(new Set());
  const textMesBoxRef = useRef();
  const shadowTopRef = useRef();
  const shadowBottomRef = useRef();
  const textMesRef = useRef();
  const sendMesRef = useRef();
  const shadowWrapper = useRef();
  useEffect(() => {
    if (textMesIncArr?.length) {
      const sortedMsgs = textMesIncArr?.map((phnObj) => {
        const sorted = [...phnObj?.textMessages]?.sort((a, b) => {
          const aNum = parseInt(a?.sentReceivedTime);
          const bNum = parseInt(b?.sentReceivedTime);
          return aNum > bNum ? 1 : -1;
        });
        return { ...phnObj, textMessages: [...sorted] };
      });
      // console.log(textMesIncArr);
      setTextMes([...sortedMsgs]);
      // Failed-bubble reconciliation: server rows NEWLY arrived via the parent
      // (not previously held here) that are outgoing + status "failed"
      // supersede matching local failedSends bubbles.
      const knownIds = new Set();
      textMes?.forEach((phnObj) =>
        phnObj?.textMessages?.forEach((m) => {
          if (m?._id) knownIds.add(m._id);
        })
      );
      sortedMsgs?.forEach((phnObj) => {
        reconcileFailedSends(
          phnObj?._id,
          phnObj?.textMessages?.filter((m) => m?._id && !knownIds.has(m._id))
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textMesIncArr]);
  useEffect(() => {
    textMesIncArr?.map((obj, index) => {
      if (obj._id === selectedPhnId) {
        setSelectedNum(index);
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhnId]);
  useEffect(() => {
    const textMesBoxEl = textMesRef.current;
    /* Lines below dereference this without `?.`. That was safe while the effect
       could only run once, post-mount, with the ref attached — it is not safe
       now that a ResizeObserver (below) measures the same element. */
    if (!textMesBoxEl) return undefined;
    if (textMesBoxEl.offsetHeight < textMesBoxEl.scrollHeight) {
      setShadowOn(true);
      const handleShadow = () => {
        const contentScroll =
          textMesBoxEl.scrollHeight - textMesBoxEl.offsetHeight;
        var currentScroll = textMesBoxEl.scrollTop / contentScroll;
        /* Optional-chained because the ResizeObserver below broke an invariant
           this line used to rely on. `shadowOn` was previously only ever set
           false by the same effect run that bound NO scroll listener, so
           "listener bound" implied "shadow elements mounted". The observer can
           now set it false while this listener is still bound — a box that
           grows until its content fits — unmounting the refs underneath it. */
        if (shadowTopRef.current) {
          shadowTopRef.current.style.opacity = currentScroll;
        }
        if (shadowBottomRef.current) {
          shadowBottomRef.current.style.opacity = 1 - currentScroll;
        }
      };
      // Bind the event listener
      textMesBoxEl.addEventListener("scroll", handleShadow);
      // textMesBoxEl?.scroll(textMesBoxEl.scrollHeight)
      return () => {
        // Unbind the event listener on clean up
        textMesBoxEl.removeEventListener("scroll", handleShadow);
      };
    }
    if (textMesBoxEl.offsetHeight >= textMesBoxEl.scrollHeight) {
      setShadowOn(false);
    }
    return undefined;
  }, [textMes]);

  /*
   * The effect above only re-measures when the MESSAGES change, so the scroll
   * shadows and the hover-scroll buttons went stale whenever the box changed
   * size instead. That was invisible while the list was capped at `70dvh` — a
   * viewport-relative height that only a window resize could alter — but inside
   * the client console the list sizes to its panel, and the panel resizes
   * without the window doing anything: the tile row rewraps, the VS header's
   * content changes, `.scc-vs-body--fill` re-solves. Hence a ResizeObserver on
   * the element, NOT a `window.resize` listener, which would miss all of those.
   *
   * Three guards, each covering a real failure mode rather than a hypothetical:
   *
   * 1. Measure inside a rAF and write state ONLY when the value changed, so a
   *    drag-resize costs at most one render per actual change of state rather
   *    than one per frame.
   * 2. Bail on a null ref. During a VS slide TWO of this component are mounted
   *    — ClientConsoleVS keeps the leaving panel until `SLIDE_MS + 40` — so the
   *    outgoing instance's observer can fire after React has cleared its ref.
   * 3. Disconnect on cleanup, or the observer outlives the unmount.
   */
  useEffect(() => {
    const textMesBoxEl = textMesRef.current;
    if (!textMesBoxEl || typeof ResizeObserver === "undefined") return undefined;

    let raf = 0;
    /* Last values this observer actually wrote. Comparing here rather than
       relying on React's bail-out is deliberate: a bail-out still costs a
       render pass, and a render inside a resize cascade is what turns two
       independent observers into a loop. An unchanged measurement must cost
       nothing — no setState, no render, no new layout. */
    let lastShadow = null;

    const measure = () => {
      raf = 0;
      const el = textMesRef.current;
      if (!el) return;
      const nextShadow = el.offsetHeight < el.scrollHeight;
      if (nextShadow === lastShadow) return;
      lastShadow = nextShadow;
      setShadowOn(nextShadow);
    };

    const observer = new ResizeObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    });
    observer.observe(textMesBoxEl);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    const textMesBox = textMesRef.current;
    /*
     * `auto`, NOT `smooth`. The thread renders at the top and then has to get
     * to the bottom; animating that trip means every open of a long thread
     * plays a visible scroll past the whole history before settling. Instant
     * puts it at the bottom on the first painted frame, so there is no travel
     * to watch. MsgPageTextDisplay has always used `auto` here, which is why
     * the messages page never had this complaint.
     */
    textMesBox?.scrollTo({ top: textMesBox.scrollHeight, behavior: "auto" });
    /*
     * The `window.scrollTo` pair that used to live here is gone (2026-10-05).
     * It positioned the DOCUMENT so the thread sat under the navbar, which
     * made sense when this component lived on the old scrolling client page.
     * Its only host now is the console's VS panel, and that page is
     * viewport-locked — the PANEL scrolls, the document does not — so those
     * calls moved nothing and the 500ms timer was pure cost.
     */
  }, [textMes, selectedNum, refocusMes, smaller900]);
  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    if (name === "sendMessageText") {
      setSendMesInput(value);
      return;
    }
  };
  /*
   * Bring the composer into view when it is focused — but AFTER the keyboard
   * has actually opened.
   *
   * The arithmetic this replaces read `visualViewport.height` during the focus
   * event, which is BEFORE the keyboard exists, so it computed a position for
   * a full-height viewport and the field ended up behind the keyboard the
   * moment one appeared. It also scrolled `window`, which does nothing here:
   * the console page is viewport-locked and the panel is the scroller.
   *
   * `scrollIntoView({ block: "nearest" })` asks the browser to scroll whatever
   * ancestor actually scrolls, which is the right answer in both a locked
   * panel and a scrolling page — and `visualViewport`'s resize is the only
   * reliable signal that the keyboard has finished opening.
   */
  const scrollInputIntoView = () => {
    const el = sendMesRef.current;
    if (!el) return;

    const vv = window.visualViewport;
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      vv?.removeEventListener("resize", run);
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };

    /* Phone: the keyboard opening resizes the visual viewport. */
    vv?.addEventListener("resize", run);
    /* Desktop, or a focus that never raises a keyboard — nothing will resize,
       so run on a short delay instead. Whichever fires first wins. */
    setTimeout(run, 350);
  };
  const inputBlur = () => {
    setRefocusMes((prev) => !prev);
  };
  // Failed-bubble reconciliation for one phone id: newly-arrived server rows
  // that are outgoing + status "failed" supersede matching local failedSends
  // bubbles. Conservative one-to-one match: same phnId + body, each server row
  // claims at most one local entry — the one whose client ts is closest to the
  // row's server time — so distinct identical-text failures aren't collapsed.
  const reconcileFailedSends = (phnId, rows) => {
    const serverFailed = rows?.filter(
      (m) => m?._id && m?.outgoingBool === true && m?.status === "failed"
    );
    if (!serverFailed?.length) return;
    setFailedSends((prev) => {
      if (!prev?.length) return prev;
      const claimed = new Set();
      serverFailed.forEach((row) => {
        let bestIdx = -1;
        let bestGap = Infinity;
        prev.forEach((f, idx) => {
          if (claimed.has(idx) || f?.phnId !== phnId || f?.body !== row?.body)
            return;
          const gap = Math.abs(
            parseInt(row?.sentReceivedTime) - parseInt(f?.ts)
          );
          const gapNum = Number.isNaN(gap) ? Number.MAX_SAFE_INTEGER : gap;
          if (gapNum < bestGap) {
            bestGap = gapNum;
            bestIdx = idx;
          }
        });
        if (bestIdx >= 0) claimed.add(bestIdx);
      });
      if (claimed.size === 0) return prev;
      return prev.filter((_, idx) => !claimed.has(idx));
    });
  };
  const doSend = async (retryBody) => {
    const body = retryBody ?? sendMesInput;
    if (!body || body.length === 0) return;
    // Send-safety guard: selectedNum (an index) lags selectedPhnId by a
    // render — if the displayed object disagrees with the selected id, the
    // switch hasn't settled. Abort rather than risk a wrong-number send.
    if (
      textMes[selectedNum]?._id &&
      textMes[selectedNum]._id !== selectedPhnId
    ) {
      if (handleSnack) handleSnack("Conversation changed — send cancelled", 4000);
      return;
    }
    // Send target comes from the selected-number PROP, never from state.
    const phnId = selectedPhnId || null;
    // Retry double-fire guard: a Retry tap for a phnId+body already in flight
    // is a no-op. (Initial sends stay unguarded — sending the same text twice
    // on purpose is legitimate.)
    const inFlightKey = `${phnId}|${body}`;
    if (retryBody != null && sendInFlightRef.current.has(inFlightKey)) return;
    const date = Date.now().toString();
    const obj = {
      textBody: body,
      localTime: date,
      phoneNumId: phnId,
      sendToNum: textMes[selectedNum]?.phoneNumber || null,
    };
    // Optimistic queued bubble (no _id) with status + null error fields.
    const optimistic = {
      body: obj.textBody,
      outgoingBool: true,
      sentReceivedTime: date,
      twilioSID: null,
      status: "queued",
      errorCode: null,
      errorMessage: null,
      statusUpdatedAt: null,
    };
    setTextMes((prev) =>
      prev?.map((phnObj) =>
        phnObj?._id === phnId
          ? {
              ...phnObj,
              textMessages: [...(phnObj?.textMessages || []), optimistic],
            }
          : phnObj
      )
    );
    if (!retryBody) setSendMesInput("");
    sendInFlightRef.current.add(inFlightKey);
    try {
      const { data } = await sendSMSgql({ variables: { ...obj } });
      const serverMsgs = data?.sendSMS?.textMessages;
      if (serverMsgs?.length > 0) {
        // Rows held before this response, for failed-bubble reconciliation.
        // (The merge below is keyed by phnId, so it stays correct even if the
        // user switched numbers while the send was in flight.)
        const knownIds = new Set(
          (
            textMes?.find((phnObj) => phnObj?._id === phnId)?.textMessages || []
          )
            .filter((m) => m?._id)
            .map((m) => m._id)
        );
        setTextMes((prev) =>
          prev?.map((phnObj) => {
            if (phnObj?._id !== phnId) return phnObj;
            const incomingKeys = new Set(
              serverMsgs.map((m) => `${m?.outgoingBool}|${m?.body}`)
            );
            const seenIds = new Set();
            const pruned = (phnObj?.textMessages || []).filter((m) => {
              if (m?._id) {
                seenIds.add(m._id);
                return true;
              }
              return !incomingKeys.has(`${m?.outgoingBool}|${m?.body}`);
            });
            const toAppend = serverMsgs.filter(
              (m) => !m?._id || !seenIds.has(m._id)
            );
            const merged = [...pruned, ...toAppend].sort((a, b) =>
              parseInt(a?.sentReceivedTime) > parseInt(b?.sentReceivedTime)
                ? 1
                : -1
            );
            return { ...phnObj, textMessages: merged };
          })
        );
        if (retryBody) {
          setFailedSends((prev) =>
            prev.filter((f) => !(f.phnId === phnId && f.body === retryBody))
          );
        }
        // Older failed rows the send response surfaced supersede local bubbles.
        reconcileFailedSends(
          phnId,
          serverMsgs.filter((m) => m?._id && !knownIds.has(m._id))
        );
      }
    } catch (error) {
      // Remove optimistic bubble, preserve typed text, surface reason, record
      // a retryable failed bubble.
      setTextMes((prev) =>
        prev?.map((phnObj) =>
          phnObj?._id === phnId
            ? {
                ...phnObj,
                textMessages: (phnObj?.textMessages || []).filter(
                  (m) => m !== optimistic
                ),
              }
            : phnObj
        )
      );
      if (!retryBody) setSendMesInput(body);
      const message = smsErrorMessage(error);
      if (handleSnack) handleSnack(message, 4000);
      setFailedSends((prev) => [
        ...prev.filter((f) => !(f.phnId === phnId && f.body === body)),
        { phnId, body, reason: message, ts: date },
      ]);
    } finally {
      sendInFlightRef.current.delete(inFlightKey);
    }
  };
  const sendMessageCall = () => {
    if (textMes[selectedNum]?.textConsent !== true) {
      const stopNote =
        textMes[selectedNum]?.textConsent === false
          ? " This recipient texted STOP — Twilio will reject it and it will show as failed."
          : "";
      const ok = window.confirm(
        `This number hasn't confirmed text consent — send anyway?${stopNote}`
      );
      if (!ok) return;
    }
    doSend();
  };
  const retryFailedSend = (body) => {
    doSend(body);
  };
  return (
    <div ref={textMesBoxRef} className="client-texts-box bdr-grad-container-heavy bdr-border-std">
      {/* HIDDEN inside the client console's VS, where the panel already has a
          "Messages" header and there is nothing to close back to. Left visible
          it did not merely look redundant — it MISBEHAVED: Close cleared
          selectedPhnId, the console immediately re-selected the first phone,
          and a two-number client was silently switched to thread 1 (which then
          got marked read). */}
      {!hideHeader && (
        <div className="client-texts-hdr-box">
          <h4>Messages</h4>
          <button
            className="client-texts-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
            onClick={() => {
              closeMessages(false);
            }}
          >
            Close
          </button>
        </div>
      )}
      <div className="client-texts-text-and-num-box">
        <div ref={shadowWrapper} className="client-texts-shadow-wrapper">
          {shadowOn && (
            <>
              <div
                ref={shadowTopRef}
                className="client-texts-shadow client-texts-shadow--top"
              ></div>
              <div
                ref={shadowBottomRef}
                className="client-texts-shadow client-texts-shadow--bottom"
              ></div>
            </>
          )}
          <div ref={textMesRef} className="client-texts-mes-box">
            {textMes[selectedNum]?.textMessages?.length > 0 ? (
              <>
                {textMes[selectedNum]?.textMessages?.map((mesObj, index) => {
                  const timeStr = BAGroupTimeFormat(
                    mesObj?.sentReceivedTime,
                    true,
                    true
                  );
                  const today = /today/gi.test(timeStr);
                  const oneYear = 1000 * 60 * 60 * 24 * 365;
                  const olderThenAYear =
                    Date.now() - oneYear > parseInt(mesObj?.sentReceivedTime);
                  const time = today
                    ? `${timeStr?.split("@")[1]}`
                    : olderThenAYear
                    ? `${timeStr?.split(",")[1]?.replace(/@/gi, "-")}`
                    : `${BAGroupTimeFormat(
                        mesObj?.sentReceivedTime,
                        true,
                        false
                      )?.replace(/@/gi, "-")}`;
                  return (
                    <div
                      key={`text-item-${index}`}
                      className={`client-texts-mes-box-${
                        mesObj?.outgoingBool ? "out" : "inc"
                      }`}
                    >
                      <div
                        className={`client-texts-mes-item client-texts-mes-item-${
                          mesObj?.outgoingBool ? "out" : "inc"
                        }`}
                      >
                        <div>{mesObj?.body}</div>
                        <div className="client-texts-mes-item-date">{`- ${time}`}</div>
                        {mesObj?.outgoingBool &&
                          mesObj?.status === "queued" && (
                            <div className="client-texts-mes-status">
                              Sending…
                            </div>
                          )}
                        {mesObj?.outgoingBool &&
                          (mesObj?.status === "failed" ||
                            mesObj?.status === "undelivered") && (
                            <div className="client-texts-mes-failed">
                              <span>
                                {mesObj?.errorMessage || "Failed to send"}
                              </span>
                              <button
                                className="client-texts-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                                onClick={() => retryFailedSend(mesObj?.body)}
                              >
                                Retry
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
                {failedSends
                  ?.filter(
                    (f) => f.phnId === textMes[selectedNum]?._id
                  )
                  ?.map((fail, failIndex) => (
                    <div
                      key={`client-failed-send-${failIndex}`}
                      className="client-texts-mes-box-out"
                    >
                      <div className="client-texts-mes-item client-texts-mes-item-out">
                        <div>{fail?.body}</div>
                        <div className="client-texts-mes-failed">
                          <span>{fail?.reason || "Failed to send"}</span>
                          <button
                            className="client-texts-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                            onClick={() => retryFailedSend(fail?.body)}
                          >
                            Retry
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            ) : (
              <div></div>
            )}
          </div>
        </div>
        <div className="client-texts-messenger-box">
          <BdrInput
            className="msg-send-field"
            inputRef={sendMesRef}
            type="text"
            label={"Message"}
            name="sendMessageText"
            onChange={handleChange}
            onFocus={scrollInputIntoView}
            onBlur={inputBlur}
            value={sendMesInput}
            multiline
            rows={1}
          />
          <button
            className={
              sendMesInput?.length > 0
                ? "client-texts-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                : "client-texts-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle send-text-dis"
            }
            disabled={!sendMesInput?.length > 0}
            onClick={sendMessageCall}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};
export default ClientTextMesDisplay;
